import { useState } from 'react';
import {
  Box,
  ButtonBase,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopyOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutlined';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import RemoveIcon from '@mui/icons-material/Remove';
import { useDeckStore } from '../store/deckStore';
import { CANVAS } from '../lib/brand';
import type { Slide } from '../types/slide';
import { slideSurfaceStyle } from '../lib/slideStyle';
import { SlideElementView } from './elements/SlideElements';

const THUMB_W = 128;
const THUMB_H = THUMB_W / (CANVAS.width / CANVAS.height);
const ZOOM_STEPS = [0.25, 0.5, 0.75, 1, 1.25, 1.5, 2];

function SlidePreview({ slide }: { slide: Slide }) {
  return (
    <div
      inert
      style={{
        ...slideSurfaceStyle(slide),
        position: 'absolute',
        left: 0,
        top: 0,
        transformOrigin: 'top left',
        transform: `scale(${THUMB_W / CANVAS.width})`,
        pointerEvents: 'none',
      }}
    >
      {slide.elements.map((el) => (
        <SlideElementView
          key={el.id}
          el={el}
          selected={false}
          scale={1}
          onSelect={() => undefined}
          onChangeText={() => undefined}
          onDragEnd={() => undefined}
        />
      ))}
    </div>
  );
}

function ZoomControl() {
  const zoom = useDeckStore((s) => s.zoom);
  const viewScale = useDeckStore((s) => s.viewScale);
  const setZoom = useDeckStore((s) => s.setZoom);
  const current = zoom ?? viewScale;
  const zoomOut = () => setZoom([...ZOOM_STEPS].reverse().find((z) => z < current - 0.01) ?? ZOOM_STEPS[0]);
  const zoomIn = () => setZoom(ZOOM_STEPS.find((z) => z > current + 0.01) ?? ZOOM_STEPS[ZOOM_STEPS.length - 1]);

  return (
    <Stack
      direction="row"
      sx={{ alignItems: 'center', p: '2px', border: 1, borderColor: 'divider', borderRadius: 2, flexShrink: 0 }}
    >
      <IconButton size="small" aria-label="Zoom out" onClick={zoomOut}>
        <RemoveIcon sx={{ fontSize: 16 }} />
      </IconButton>
      <Tooltip title={zoom === null ? 'Fit to screen' : 'Click to fit'}>
        <ButtonBase
          onClick={() => setZoom(null)}
          sx={{ width: 48, height: 32, borderRadius: 1.5, fontSize: 12, fontWeight: 500, color: 'neutral.main' }}
        >
          {Math.round(current * 100)}%
        </ButtonBase>
      </Tooltip>
      <IconButton size="small" aria-label="Zoom in" onClick={zoomIn}>
        <AddIcon sx={{ fontSize: 16 }} />
      </IconButton>
    </Stack>
  );
}

export function Filmstrip() {
  const slides = useDeckStore((s) => s.slides);
  const activeSlideIndex = useDeckStore((s) => s.activeSlideIndex);
  const setActiveSlide = useDeckStore((s) => s.setActiveSlide);
  const addSlide = useDeckStore((s) => s.addSlide);
  const duplicateSlide = useDeckStore((s) => s.duplicateSlide);
  const deleteSlide = useDeckStore((s) => s.deleteSlide);
  const reorderSlides = useDeckStore((s) => s.reorderSlides);
  const [menu, setMenu] = useState<{ index: number; anchor?: HTMLElement; x?: number; y?: number } | null>(null);

  const closeMenu = () => setMenu(null);

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 2,
        flexShrink: 0,
        px: 2,
        py: 1.5,
        bgcolor: 'background.paper',
        borderTop: 1,
        borderColor: 'divider',
      }}
    >
      <Box sx={{ display: 'flex', flex: 1, minWidth: 0, alignItems: 'flex-end', gap: 1.5, overflowX: 'auto', p: 0.5 }}>
        {slides.map((slide, i) => {
          const active = i === activeSlideIndex;
          return (
            <Box
              key={slide.id}
              sx={{ display: 'flex', alignItems: 'flex-end', gap: 1, flexShrink: 0, '&:hover .slide-more': { opacity: 1 } }}
            >
              <Typography
                variant="caption"
                sx={{ width: 14, textAlign: 'right', fontWeight: 500, color: active ? 'primary.main' : 'text.disabled' }}
              >
                {i + 1}
              </Typography>
              <Box sx={{ position: 'relative' }}>
                <ButtonBase
                  aria-label={`Slide ${i + 1}`}
                  aria-current={active ? 'true' : undefined}
                  draggable
                  onDragStart={(e) => e.dataTransfer.setData('slide-index', String(i))}
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={(e) => {
                    e.preventDefault();
                    const from = Number(e.dataTransfer.getData('slide-index'));
                    if (!Number.isNaN(from)) reorderSlides(from, i);
                  }}
                  onClick={() => setActiveSlide(i)}
                  onContextMenu={(e) => {
                    e.preventDefault();
                    setActiveSlide(i);
                    setMenu({ index: i, x: e.clientX, y: e.clientY });
                  }}
                  sx={(t) => ({
                    display: 'block',
                    position: 'relative',
                    overflow: 'hidden',
                    width: THUMB_W,
                    height: THUMB_H,
                    borderRadius: 1,
                    border: 1,
                    borderColor: 'divider',
                    boxShadow: active ? `0 0 0 2px ${t.palette.primary.main}` : 'none',
                    transition: 'box-shadow 120ms',
                    '&:hover': {
                      boxShadow: `0 0 0 2px ${active ? t.palette.primary.main : t.palette.chrome.border}`,
                    },
                  })}
                >
                  <SlidePreview slide={slide} />
                </ButtonBase>
                <IconButton
                  className="slide-more"
                  size="small"
                  aria-label={`Slide ${i + 1} options`}
                  onClick={(e) => {
                    setActiveSlide(i);
                    setMenu({ index: i, anchor: e.currentTarget });
                  }}
                  sx={{
                    position: 'absolute',
                    top: 4,
                    right: 4,
                    width: 24,
                    height: 24,
                    opacity: menu?.index === i ? 1 : 0,
                    bgcolor: 'background.paper',
                    boxShadow: 1,
                    '&:hover': { bgcolor: 'background.paper' },
                    '&.Mui-focusVisible': { opacity: 1 },
                  }}
                >
                  <MoreHorizIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </Box>
            </Box>
          );
        })}
        <Tooltip title="Add slide">
          <ButtonBase
            aria-label="Add slide"
            onClick={() => addSlide()}
            sx={{
              flexShrink: 0,
              width: THUMB_W,
              height: THUMB_H,
              ml: 2.75,
              borderRadius: 1,
              border: '1.5px dashed',
              borderColor: 'chrome.border',
              color: 'text.secondary',
              '&:hover': { borderColor: 'primary.main', color: 'primary.main', bgcolor: 'chrome.tint' },
            }}
          >
            <AddIcon />
          </ButtonBase>
        </Tooltip>
      </Box>

      <ZoomControl />

      <Menu
        open={Boolean(menu)}
        onClose={closeMenu}
        anchorEl={menu?.anchor}
        anchorReference={menu?.anchor ? 'anchorEl' : 'anchorPosition'}
        anchorPosition={menu?.x !== undefined ? { left: menu.x, top: menu.y ?? 0 } : undefined}
        slotProps={{ paper: { sx: { width: 180 } } }}
      >
        <MenuItem
          onClick={() => {
            if (menu) duplicateSlide(menu.index);
            closeMenu();
          }}
        >
          <ListItemIcon>
            <ContentCopyOutlinedIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="Duplicate" />
        </MenuItem>
        <MenuItem
          disabled={slides.length <= 1}
          onClick={() => {
            if (menu) deleteSlide(menu.index);
            closeMenu();
          }}
          sx={{ color: 'error.main' }}
        >
          <ListItemIcon sx={{ color: 'inherit' }}>
            <DeleteOutlineIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="Delete" />
        </MenuItem>
      </Menu>
    </Box>
  );
}
